// Paginación de tablas antd en un solo lugar: el objeto `pagination`
// (placement, showSizeChanger, showQuickJumper, pageSizeOptions, showTotal con
// i18n) y el handler de cambio (reset a página 1 cuando cambia el page size)
// estaban copiados en 5 listas.
import { useState } from 'react';
import type { TablePaginationConfig } from 'antd';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export interface PaginacionPropsConfig {
  page: number;
  pageSize: number;
  total: number;
  /** Desactiva el selector de tamaño de página (tablas con tamaño fijo). */
  showSizeChanger?: boolean;
}

export function paginationProps(
  t: Translate,
  { page, pageSize, total, showSizeChanger = true }: PaginacionPropsConfig,
): TablePaginationConfig {
  return {
    current: page,
    pageSize,
    total,
    placement: ['bottomEnd'],
    showSizeChanger,
    showQuickJumper: false,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showTotal: (totalCount, range) =>
      t('pagination.showTotal', { from: range[0], to: range[1], total: totalCount }),
  };
}

/**
 * Handler de Table onChange para paginación: cambia el page size (quien
 * recibe el callback decide si resetea la página) o pagina.
 */
export function makePaginacionHandler(
  currentPageSize: number,
  onPageSizeChange: (pageSize: number) => void,
  onPageChange: (page: number) => void,
) {
  return (pagination: TablePaginationConfig) => {
    const nextPageSize = pagination.pageSize ?? currentPageSize;
    if (nextPageSize !== currentPageSize) {
      onPageSizeChange(nextPageSize);
    } else {
      onPageChange(pagination.current ?? 1);
    }
  };
}

export interface PaginacionState {
  page: number;
  pageSize: number;
  handleChange: (pagination: TablePaginationConfig) => void;
  setPage: (page: number) => void;
}

export function usePaginacion(initialPageSize = 10): PaginacionState {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const handleChange = makePaginacionHandler(
    pageSize,
    (nextPageSize) => {
      setPageSize(nextPageSize);
      setPage(1);
    },
    setPage,
  );

  return { page, pageSize, handleChange, setPage };
}
